import {
  Accordion,
  BrandIcon,
  ClearIcon,
  FontSizeEnum,
  FontWeightEnum,
  IconButton,
  IconButtonSizeEnum,
  IconSizeEnum,
  RelevantPeopleIcon,
  ScreenTextOcrIcon,
  STTIcon,
  Typography,
  VideoCutsCortesIcon,
} from '@acceso/design-system';
import { useEffect, useState } from 'react';
import { FieldValues, Path, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { CheckboxConfirmation } from './CheckboxConfirmation';
import { CheckboxForm } from './CheckboxForm';

export type InputEnrichmentsProps<T extends FieldValues> = {
  form: UseFormReturn<T>;

  /**
   * @default
   */
  isRadio?: boolean;
};

export function InputEnrichments<T extends FieldValues>({
  form,
  isRadio,
}: InputEnrichmentsProps<T>) {
  const { t } = useTranslation();
  const [isEnrichmentAccordionOpen, setIsEnrichmentAccordionOpen] = useState(false);

  const resetEnrichments = () => {
    // @ts-ignore
    form.setValue('parameters.enrichments.advertising', false);
    // @ts-ignore
    form.setValue('parameters.enrichments.logos', false);
    // @ts-ignore
    form.setValue('parameters.enrichments.ocr', false);
    // @ts-ignore
    form.setValue('parameters.enrichments.peoples', false);
    // @ts-ignore
    form.setValue('parameters.enrichments.stt', false);
  };

  /**
   * Resets enrichments if selection is for radio to avoid leaving checked
   * a non-radio feature disabled.
   */
  useEffect(() => {
    if (isRadio) {
      form.resetField('parameters.enrichments' as Path<T>);
    }
  }, [form.resetField, isRadio]);

  return (
    <div className="mt-7 w-full">
      <Accordion
        title={t('Enriquecimiento')}
        isOpen={isEnrichmentAccordionOpen}
        onOpen={() => setIsEnrichmentAccordionOpen(!isEnrichmentAccordionOpen)}
        lineSeparator
      >
        <div className="mb-2 flex items-center justify-end gap-2">
          <Typography fontSize={FontSizeEnum.Xs} weight={FontWeightEnum.Semibold}>
            {t('Limpiar')}
          </Typography>

          <IconButton className="-mr-1" size={IconButtonSizeEnum.SM} onClick={resetEnrichments}>
            <ClearIcon size={IconSizeEnum.XS} />
          </IconButton>
        </div>

        <div className="flex flex-col gap-3">
          <CheckboxForm
            form={form}
            name={'parameters.enrichments.ocr' as Path<T>}
            label={t('Texto en pantalla (OCR)')}
            icon={<ScreenTextOcrIcon size={IconSizeEnum.MD} />}
            classname="pl-8"
          />
          <hr></hr>
          <CheckboxForm
            form={form}
            name={'parameters.enrichments.stt' as Path<T>}
            label={t('Activar transcripción de texto')}
            icon={<STTIcon size={IconSizeEnum.MD} />}
            classname="pl-8"
            mapChange={(onChange, event) => {
              if (event.currentTarget.value === 'true') {
                // @ts-ignore
                form.setValue('parameters.enrichments.advertising', false);
              }

              onChange(event);
            }}
          />
          <hr></hr>
          <CheckboxForm
            form={form}
            name={'parameters.enrichments.logos' as Path<T>}
            label={t('Localizar logos')}
            icon={<BrandIcon size={IconSizeEnum.MD} />}
            classname="pl-8"
            disabled={isRadio}
          />
          <hr></hr>
          <CheckboxForm
            form={form}
            name={'parameters.enrichments.peoples' as Path<T>}
            label={t('Personas de interés')}
            icon={<RelevantPeopleIcon size={IconSizeEnum.MD} />}
            classname="pl-8"
            disabled={isRadio}
          />

          <hr></hr>

          <CheckboxConfirmation
            form={form}
            name={'parameters.enrichments.advertising' as Path<T>}
            label={t('Cortes automáticos')}
            icon={<VideoCutsCortesIcon size={IconSizeEnum.MD} />}
            classname="pl-8"
          />
        </div>
      </Accordion>
    </div>
  );
}
